/**
 * RoadmapViewer — Mountain View session picker
 *
 * Lists the user's roadmap sessions and navigates directly to the mountain
 * visualization (/roadmap/mountain/:sessionId) on click. Provides a
 * visualization-first path from the sidebar without going through the grid.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Spin } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { LayoutGrid, Search } from "lucide-react";
import type { SessionData } from "../Modus/RoadmapBuilder/types";
import { formatPeakDate } from "../Modus/RoadmapBuilder/timeframe";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

const TILE_ACCENT = "#f5f3ff"; // light purple tint

const SessionTile = ({
  session,
  onView,
  onGrid,
}: {
  session: SessionData;
  onView: () => void;
  onGrid: () => void;
}) => {
  const peakLabel = session.peakDate ? formatPeakDate(session.peakDate) : null;

  return (
    <button
      onClick={onView}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        borderRadius: 12,
        border: "1px solid transparent",
        background: TILE_ACCENT,
        cursor: "pointer",
        textAlign: "left",
        width: "100%",
        transition: "box-shadow 0.15s, border-color 0.15s",
        fontFamily: '"Albert Sans", sans-serif',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.09)";
        e.currentTarget.style.borderColor = "#ddd6fe";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = "transparent";
      }}
    >
      {/* Icon */}
      <span style={{ fontSize: 22, flexShrink: 0 }}>⛰️</span>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b",
          fontFamily: '"Albert Sans", sans-serif', marginBottom: 1 }}>
          {session.companyName}
        </div>
        <div style={{ fontSize: 10, lineHeight: 1.4,
          fontFamily: "monospace", color: "hsl(var(--rb-primary) / 0.7)" }}>
          {peakLabel ? `Peak: ${peakLabel}` : "No peak date set"}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        {/* Grid icon — stops propagation so it doesn't trigger tile click */}
        <div
          onClick={(e) => { e.stopPropagation(); onGrid(); }}
          title="Open in Grid View"
          style={{ width: 26, height: 26, borderRadius: "50%",
            background: "rgba(0,0,0,0.05)", display: "flex",
            alignItems: "center", justifyContent: "center", cursor: "pointer" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(0,0,0,0.10)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "rgba(0,0,0,0.05)"; }}
        >
          <LayoutGrid style={{ width: 11, height: 11, color: "#64748b" }} />
        </div>

        {/* Arrow → mountain */}
        <div style={{ width: 26, height: 26, borderRadius: "50%",
          background: "rgba(0,0,0,0.06)", display: "flex",
          alignItems: "center", justifyContent: "center" }}>
          <RightOutlined style={{ fontSize: 10, color: "#64748b" }} />
        </div>
      </div>
    </button>
  );
};

export const RoadmapViewer = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [advisingSessions, setAdvisingSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    Promise.all([
      fetch(`${SERVER_URL}/api/rb/sessions`, { credentials: "include" }),
      fetch(`${SERVER_URL}/api/rb/advisor/sessions`, { credentials: "include" }),
    ])
      .then(async ([sessRes, advRes]) => {
        if (sessRes.ok) setSessions(await sessRes.json());
        if (advRes.ok) setAdvisingSessions(await advRes.json());
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const q = search.toLowerCase().trim();
  const owned = sessions.filter((s) => !s.collaboratorRole);
  const shared = sessions.filter((s) => !!s.collaboratorRole);

  const filter = (list: SessionData[]) =>
    q ? list.filter((s) => s.companyName.toLowerCase().includes(q)) : list;

  const toMountain = (id: string) => navigate(`/roadmap/mountain/${id}`);
  const toGrid = (id: string) => navigate(`/roadmap/build_roadmap/${id}`);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <Spin size="large" />
      </div>
    );
  }

  const allEmpty = owned.length === 0 && shared.length === 0 && advisingSessions.length === 0;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1
          style={{ fontFamily: '"Albert Sans", sans-serif' }}
          className="text-2xl font-bold text-gray-900 mb-1"
        >
          View Roadmap
        </h1>
        <p className="text-sm text-gray-400">
          Select a roadmap to view the mountain visualization.
        </p>
      </div>

      {!allEmpty && (
        <Input
          prefix={<Search className="h-3.5 w-3.5 text-muted-foreground" style={{ flexShrink: 0 }} />}
          placeholder="Search roadmaps..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-5"
          allowClear
        />
      )}

      {allEmpty ? (
        <div className="text-center py-16 text-muted-foreground">
          <span style={{ fontSize: 40, display: "block", marginBottom: 12, opacity: 0.35 }}>⛰️</span>
          <p className="text-sm">No roadmaps yet.</p>
          <button
            onClick={() => navigate("/roadmap/new")}
            style={{ marginTop: 16, padding: "6px 16px", borderRadius: 8,
              background: "hsl(var(--rb-primary))", color: "white", border: "none",
              cursor: "pointer", fontSize: 13, fontFamily: '"Albert Sans", sans-serif',
              fontWeight: 600 }}
          >
            Create your first roadmap
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {filter(owned).length > 0 && (
            <section>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                My Roadmaps
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
                {filter(owned).map((s) => (
                  <SessionTile key={s.id} session={s}
                    onView={() => toMountain(s.id)} onGrid={() => toGrid(s.id)} />
                ))}
              </div>
            </section>
          )}

          {filter(shared).length > 0 && (
            <section>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Shared With Me
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
                {filter(shared).map((s) => (
                  <SessionTile key={s.id} session={s}
                    onView={() => toMountain(s.id)} onGrid={() => toGrid(s.id)} />
                ))}
              </div>
            </section>
          )}

          {filter(advisingSessions).length > 0 && (
            <section>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                Advising
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
                {filter(advisingSessions).map((s) => (
                  <SessionTile key={s.id} session={s}
                    onView={() => toMountain(s.id)} onGrid={() => toGrid(s.id)} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};
